import type { SourceFilter } from '@uipath/portal-shell/loader';

import type { IDirectoryEntry } from '../../common/interfaces/cis/directory';
import { DirectoryEntityType } from '../../common/interfaces/cis/directory';
import {
    get,
    post,
} from '../utility/Requests';
import { getIdentityBaseRoute } from './IdentityServiceUtils';

export const userDirectoryUrl = `/api/identity/Directory`;

// Helper function to get direct Identity service URL
const getDirectDirectoryUrl = (partitionGlobalId: string, isHostMode: boolean = false) =>
    `${getIdentityBaseRoute(partitionGlobalId, isHostMode)}/api/Directory`;

export function getDirectoryEntities(
    {
        url: _url, partitionGlobalId, sourceFilter, startsWith, isHostMode = false,
    }: {
        url: string;
        partitionGlobalId: string;
        sourceFilter: SourceFilter[];
        startsWith: string;
        isHostMode?: boolean;
    }) {
    const directUrl = `${getDirectDirectoryUrl(partitionGlobalId, isHostMode)}/Search/${partitionGlobalId}`;
    return get<IDirectoryEntry[]>(directUrl, {
        urlParams: {
            sourceFilter,
            startsWith,
        },
        ignoreBasePath: true,
    });
}

export function resolveByName(
    {
        url: _url, partitionGlobalId, entityName, entityType, isHostMode = false,
    }: {
        url: string;
        partitionGlobalId: string;
        entityName: string;
        entityType: DirectoryEntityType;
        isHostMode?: boolean;
    }) {
    const directUrl = `${getDirectDirectoryUrl(partitionGlobalId, isHostMode)}/ResolveByName/${partitionGlobalId}`;
    return post<IDirectoryEntry>(directUrl, {
        body: {
            entityName,
            entityType: entityType === DirectoryEntityType.user ? 'user' : 'group',
        },
        ignoreBasePath: true,
    });
}

export function resolveById(
    {
        url: _url, partitionGlobalId, entityId, entityType, groupIds, isHostMode = false,
    }: {
        url?: string;
        partitionGlobalId: string;
        entityId: string;
        entityType: DirectoryEntityType;
        groupIds?: string[];
        isHostMode?: boolean;
    }) {
    const directUrl = `${getDirectDirectoryUrl(partitionGlobalId, isHostMode)}/Resolve/${partitionGlobalId}`;
    return post<IDirectoryEntry>(directUrl, {
        body: {
            entityId,
            entityType: entityType === DirectoryEntityType.user ? 'user' : 'group',
            groupIds,
        },
        ignoreBasePath: true,
    });
}

export async function bulkResolveByName(
    {
        url = userDirectoryUrl, partitionGlobalId, entityNames, entityType, isHostMode = false,
    }: {
        url?: string;
        partitionGlobalId: string;
        entityNames: string[];
        entityType: DirectoryEntityType;
        isHostMode?: boolean;
    }) {
    const directUrl = getDirectDirectoryUrl(partitionGlobalId, isHostMode);
    const chunkSize = 20; // max chunk size for identity BulkResolveByName API is 20
    const chunks = [];

    for (let i = 0; i < entityNames.length; i += chunkSize) {
        chunks.push(entityNames.slice(i, i + chunkSize));
    }

    const resolvedChunks = await Promise.all(chunks.map(chunk =>
        post<Record<string, IDirectoryEntry>>(`${directUrl}/BulkResolveByName/${partitionGlobalId}`, {
            body: {
                entityNames: chunk,
                entityType: entityType === DirectoryEntityType.user ? 'user' : 'group',
            },
            ignoreBasePath: true,
        })
    ));

    return resolvedChunks.flatMap(chunk => Object.values(chunk));
}
